import {
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Form,
  FormInstance,
  Menu,
  Radio,
  Row,
  Space,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import ReportSelectMonthFields from '@/components/_common/ReportSelectMonthFields';
import { SituationOptions } from '@/core/domain/reports/movements/workOrders/WorkOrderReport.types';
import { CheckboxGroupButtons } from '@/components/_common';
import MoreOptionsDailyMovementReport from './MoreOptionsDailyMovementReport/index';
import { Info } from 'react-feather';
import { DateFilterByTypes } from '@/core/domain/NetReport/WorkOrder/WorkOrder.types';
import {
  DailyMovementRequestForm,
  TypeReportTypes,
} from '@/core/domain/reports/movements/dailyMovement/dailyMovement.types';

interface Props {
  form: FormInstance<DailyMovementRequestForm>;
}

const DailyMovementReportForm: React.FC<Props> = ({ form }) => {
  const { Title } = Typography;
  const intl = useIntl();

  const situationOptions = [
    {
      label: intl.formatMessage({
        id: 'movementDailyMovementReport.form.situation.open',
      }),
      value: SituationOptions.open,
    },
    {
      label: intl.formatMessage({
        id: 'movementDailyMovementReport.form.situation.authorized',
      }),
      value: SituationOptions.authorized,
    },
    {
      label: intl.formatMessage({
        id: 'movementDailyMovementReport.form.situation.closed',
      }),
      value: SituationOptions.closed,
    },
    {
      label: intl.formatMessage({
        id: 'movementDailyMovementReport.form.situation.canceled',
      }),
      value: SituationOptions.canceled,
    },
  ];

  const expensesIndividuallyMenu = (
    <Menu mode="vertical" style={{ width: 300 }}>
      <Menu.Item key={1}>
        {intl.formatMessage({
          id: 'movementDailyMovementReport.form.filterByDate.info',
        })}
      </Menu.Item>
    </Menu>
  );

  return (
    <Form layout="vertical" form={form}>
      <ReportSelectMonthFields
        form={form}
        dateFilter={
          <>
            <Title level={5}>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.filterByDate',
              })}
            </Title>

            <Space align="center">
              <Form.Item
                name="dateFilterBy"
                initialValue={DateFilterByTypes.entry}
                noStyle
              >
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  size="large"
                >
                  <Radio value={DateFilterByTypes.entry}>
                    {intl.formatMessage({
                      id: 'movementDailyMovementReport.form.filterByDate.entrance',
                    })}
                  </Radio>

                  <Radio value={DateFilterByTypes.authorization}>
                    {intl.formatMessage({
                      id: 'movementDailyMovementReport.form.filterByDate.authorization',
                    })}
                  </Radio>

                  <Radio value={DateFilterByTypes.closing}>
                    {intl.formatMessage({
                      id: 'movementDailyMovementReport.form.filterByDate.shutdown',
                    })}
                  </Radio>

                  <Radio value={DateFilterByTypes.leave}>
                    {intl.formatMessage({
                      id: 'movementDailyMovementReport.form.filterByDate.exit',
                    })}
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Dropdown
                overlay={expensesIndividuallyMenu}
                trigger={['click']}
                arrow
                placement="topCenter"
              >
                <Info size={20} id="note-type-icon" cursor={'pointer'} />
              </Dropdown>
            </Space>
          </>
        }
      />

      <Row gutter={[12, 12]}>
        <Col sm={24}>
          <Title level={5}>
            {intl.formatMessage({
              id: 'movementDailyMovementReport.form.situation',
            })}
          </Title>

          <Form.Item
            name="situation"
            initialValue={[SituationOptions.authorized]}
            noStyle
          >
            <CheckboxGroupButtons options={situationOptions} />
          </Form.Item>
        </Col>

        <Col sm={24}>
          <Title level={5}>
            {intl.formatMessage({
              id: 'movementDailyMovementReport.form.groupReportBy',
            })}
          </Title>

          <Form.Item name="typeReport" initialValue={TypeReportTypes.DATE}>
            <Radio.Group optionType="button" buttonStyle="solid" size="large">
              <Radio value={TypeReportTypes.DATE}>
                {intl.formatMessage({
                  id: 'movementDailyMovementReport.form.groupReportBy.date',
                })}
              </Radio>

              <Radio value={TypeReportTypes.EMPLOYEE}>
                {intl.formatMessage({
                  id: 'movementDailyMovementReport.form.groupReportBy.estimatorAndSalesperson',
                })}
              </Radio>

              <Radio value={TypeReportTypes.CUSTOMER}>
                {intl.formatMessage({
                  id: 'movementDailyMovementReport.form.groupReportBy.customer',
                })}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col sm={4}>
          <Form.Item
            noStyle
            name="parts"
            initialValue={true}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.parts',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col sm={4}>
          <Form.Item
            noStyle
            name="suppliedParts"
            initialValue={false}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.partsProvided',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col sm={4}>
          <Form.Item
            noStyle
            name="services"
            initialValue={true}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.tasks',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col sm={4}>
          <Form.Item
            noStyle
            name="thirdParty"
            initialValue={true}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.third',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col sm={4}>
          <Form.Item
            noStyle
            name="directSale"
            initialValue={true}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.directSale',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col sm={4}>
          <Form.Item
            noStyle
            name="summary"
            initialValue={false}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.synthetic',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            noStyle
            name="showMlb"
            initialValue={false}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'movementDailyMovementReport.form.checkbox.showMlb',
              })}
            </Checkbox>
          </Form.Item>
        </Col>

        <Divider />

        <MoreOptionsDailyMovementReport />
      </Row>
    </Form>
  );
};

export default DailyMovementReportForm;
